import { MutableRefObject, useEffect, useRef } from 'react';
import {
  BrushBehavior as d3BrushBehavior,
  brush as d3brush,
  ScaleLinear as d3ScaleLinear,
  select as d3select,
} from 'd3';

export interface UseBrushProps {
  brushRef: MutableRefObject<d3BrushBehavior<unknown> | undefined>;
  svgRef: MutableRefObject<SVGSVGElement | SVGGElement | undefined>;
  extent?: [[number, number], [number, number]];
  scaleX: d3ScaleLinear<number, number>;
  scaleY: d3ScaleLinear<number, number>;
  isBrushable?: boolean;
  isGridded?: boolean;
  onBrush?: (
    selection: [[number, number], [number, number]] | null,
    scaleX: d3ScaleLinear<number, number>,
    scaleY: d3ScaleLinear<number, number>
  ) => void;
  onBrushEnd?: (
    selection: [[number, number], [number, number]] | null,
    scaleX: d3ScaleLinear<number, number>,
    scaleY: d3ScaleLinear<number, number>
  ) => void;
}

const getRoundedIntermediaryPoint = (
  selection: [[number, number], [number, number]],
  scaleX: d3ScaleLinear<number, number>,
  scaleY: d3ScaleLinear<number, number>
) => {
  if (!selection) {
    return null;
  }

  const xTicks = scaleX.ticks(20);
  const xDelta = xTicks[1] - xTicks[0];
  const yTicks = scaleY.ticks(20);
  const yDelta = yTicks[1] - yTicks[0];

  return [
    [
      scaleX(
        Math.round(scaleX.invert(selection[0][0]) / xDelta) * xDelta -
          xDelta / 2
      ),
      scaleY(
        Math.round(scaleY.invert(selection[0][1]) / yDelta) * yDelta -
          yDelta / 2
      ),
    ],
    [
      scaleX(
        Math.round(scaleX.invert(selection[1][0]) / xDelta) * xDelta -
          xDelta / 2
      ),

      scaleY(
        Math.round(scaleY.invert(selection[1][1]) / yDelta) * yDelta -
          yDelta / 2
      ),
    ],
  ] as [[number, number], [number, number]];
};

export const useBrush = ({
  brushRef: propsBrushRef,
  svgRef,
  extent,
  scaleX,
  scaleY,
  isBrushable,
  isGridded,
  onBrush,
  onBrushEnd,
}: UseBrushProps) => {
  const brushRef = propsBrushRef || useRef<d3BrushBehavior<unknown>>();

  useEffect(() => {
    if (svgRef.current && isBrushable) {
      brushRef.current = d3brush()
        .extent(extent!)
        .keyModifiers(false)
        .filter((event) => event.shiftKey)
        .on('brush', (event) => {
          if (!event.sourceEvent || !brushRef.current || !svgRef.current)
            return;

          if (isGridded) {
            const selection = getRoundedIntermediaryPoint(
              event.selection,
              scaleX,
              scaleY
            );
            d3select(svgRef.current).call(
              brushRef.current.move as any,
              selection
            );
            onBrush?.(selection, scaleX, scaleY);
          } else {
            onBrush?.(event.selection, scaleX, scaleY);
          }
        })
        .on('end', (event) => {
          if (!event.sourceEvent || !brushRef.current || !svgRef.current)
            return;

          onBrushEnd?.(event.selection, scaleX, scaleY);
        });
      d3select(svgRef.current).call(brushRef.current as any);
    }
  });
};
